/**
 * Client-side rules aligned with ProductOrderController / StandingOrderExecutionService.
 * Request body keys are camelCase (branchId, customerCode, sourceAccount, …).
 * Backend remains authoritative.
 */

/** @type {{ label: string, value: string }[]} */
export const STANDING_ORDER_CIRCLE_OPTIONS = [
	{ label: "Daily", value: "daily" },
	{ label: "Weekly", value: "weekly" },
	{ label: "Monthly", value: "monthly" },
	{ label: "Yearly", value: "annually" },
];

const CIRCLE_SET = new Set(["daily", "weekly", "monthly", "annually", "annual"]);

/** Relative to axios baseURL (already includes `/api/v1`). */
export const STANDING_ORDER_API = {
	create: "/standing-orders/orders",
	show: (id) => `/standing-orders/order/${encodeURIComponent(id)}`,
	update: (id) => `/standing-orders/order/${encodeURIComponent(id)}`,
	delete: (id) => `/standing-orders/order/${encodeURIComponent(id)}`,
	list: "/standing-orders/orders",
};

export function normalizeCustomerCode(code) {
	if (code == null || code === "") {
		return "";
	}
	return String(code).trim();
}

export function normalizeAccountNumber(n) {
	if (n == null || n === "") {
		return "";
	}
	return String(n).trim();
}

/**
 * COA subhead id from account row (accounts/paginate / ProductOrder relations).
 * @param {Record<string, unknown> | null | undefined} account
 * @returns {number|null}
 */
export function extractCoaSubheadIdFromAccount(account) {
	if (!account || typeof account !== "object") {
		return null;
	}
	const at = /** @type {Record<string, unknown>} */ (account).account_type;
	if (!at || typeof at !== "object") {
		return null;
	}
	const t = /** @type {Record<string, unknown>} */ (at);
	if (t.coa_subhead_id != null && t.coa_subhead_id !== "") {
		const n = Number(t.coa_subhead_id);
		return Number.isFinite(n) && n > 0 ? n : null;
	}
	const sh = t.coa_subhead;
	if (sh && typeof sh === "object" && /** @type {Record<string, unknown>} */ (sh).id != null) {
		const n = Number(/** @type {Record<string, unknown>} */ (sh).id);
		return Number.isFinite(n) && n > 0 ? n : null;
	}
	return null;
}

/**
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeStandingOrderCircle(raw) {
	if (raw == null || raw === "") {
		return null;
	}
	if (typeof raw === "object" && raw !== null && "value" in raw) {
		raw = /** @type {{ value?: unknown }} */ (raw).value;
	}
	const s = String(raw).trim().toLowerCase();
	if (s === "yearly") {
		return "annually";
	}
	if (s === "annual" || s === "annually") {
		return s;
	}
	return CIRCLE_SET.has(s) ? s : null;
}

/**
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateStandingOrderDateRange(startDate, endDate) {
	const a = String(startDate || "").trim();
	const b = String(endDate || "").trim();
	if (!a || !b) {
		return { ok: false, message: "Start date and end date are required." };
	}
	const t0 = Date.parse(a);
	const t1 = Date.parse(b);
	if (!Number.isFinite(t0) || !Number.isFinite(t1)) {
		return { ok: false, message: "Enter valid start and end dates." };
	}
	if (t0 > t1) {
		return {
			ok: false,
			message: "Start date must be on or before end date.",
		};
	}
	return { ok: true };
}

/**
 * @param {string|null|undefined} circleNormalized
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateStandingOrderCirclePresent(circleNormalized) {
	if (circleNormalized == null || circleNormalized === "") {
		return {
			ok: false,
			message:
				"Select a schedule: daily, weekly, monthly, or yearly (annually).",
		};
	}
	if (!CIRCLE_SET.has(circleNormalized)) {
		return {
			ok: false,
			message:
				"Invalid schedule. Use daily, weekly, monthly, or annually (annual is also accepted).",
		};
	}
	return { ok: true };
}

/**
 * @param {{ customer?: { code?: string } } | null|undefined} sourceAccount
 * @param {string} orderingCustomerCode
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateSourceBelongsToOrderingCustomer(
	sourceAccount,
	orderingCustomerCode,
) {
	const want = normalizeCustomerCode(orderingCustomerCode);
	if (!want) {
		return { ok: false, message: "Ordering customer is required." };
	}
	if (!sourceAccount || !sourceAccount.customer) {
		return {
			ok: false,
			message:
				"Source account must belong to the selected customer.",
		};
	}
	const got = normalizeCustomerCode(sourceAccount.customer.code);
	if (!got) {
		return {
			ok: false,
			message:
				"Source account must belong to the selected customer.",
		};
	}
	if (got.toLowerCase() !== want.toLowerCase()) {
		return {
			ok: false,
			message:
				"Source account must belong to the selected customer.",
		};
	}
	return { ok: true };
}

/**
 * @param {string} sourceNumber
 * @param {string} destinationNumber
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateDistinctAccountNumbers(sourceNumber, destinationNumber) {
	const a = normalizeAccountNumber(sourceNumber);
	const b = normalizeAccountNumber(destinationNumber);
	if (!a || !b) {
		return {
			ok: false,
			message: "Source and destination account numbers are required.",
		};
	}
	if (a.toLowerCase() === b.toLowerCase()) {
		return {
			ok: false,
			message:
				"Source and destination account numbers must be different.",
		};
	}
	return { ok: true };
}

/**
 * @param {unknown} amount
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validatePositiveAmount(amount) {
	const n =
		typeof amount === "number"
			? amount
			: Number(String(amount ?? "").replace(/,/g, ""));
	if (!Number.isFinite(n) || n <= 0) {
		return {
			ok: false,
			message: "Enter a valid amount greater than zero.",
		};
	}
	return { ok: true };
}

/**
 * @param {unknown} branchId
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateBranchId(branchId) {
	const n = branchId == null || branchId === "" ? NaN : Number(branchId);
	if (!Number.isFinite(n) || n < 1) {
		return {
			ok: false,
			message: "Branch is required. Reload the page or contact support.",
		};
	}
	return { ok: true };
}

/**
 * @param {unknown} sourceCoa
 * @param {unknown} destinationCoa
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateCoaSubheadIds(sourceCoa, destinationCoa) {
	const a = sourceCoa == null || sourceCoa === "" ? NaN : Number(sourceCoa);
	const b =
		destinationCoa == null || destinationCoa === ""
			? NaN
			: Number(destinationCoa);
	if (!Number.isFinite(a) || a < 1 || !Number.isFinite(b) || b < 1) {
		return {
			ok: false,
			message:
				"Source and destination COA subheads are required. Select accounts whose product mapping includes COA, or load the order again.",
		};
	}
	return { ok: true };
}

/**
 * Resolve COA subhead id: explicit form fields (e.g. after API load), else from account row.
 * @param {Record<string, unknown>} form
 * @param {'source' | 'destination'} side
 * @returns {number|null}
 */
export function resolveCoaSubheadIdForPayload(form, side) {
	const key = side === "source" ? "source_coa_id" : "destination_coa_id";
	const raw = form[key];
	if (raw != null && raw !== "") {
		const n = Number(raw);
		if (Number.isFinite(n) && n > 0) {
			return n;
		}
	}
	const acc =
		side === "source"
			? /** @type {unknown} */ (form.source_account)
			: /** @type {unknown} */ (form.destination_account);
	return extractCoaSubheadIdFromAccount(
		acc && typeof acc === "object"
			? /** @type {Record<string, unknown>} */ (acc)
			: null,
	);
}

/**
 * Build JSON body for POST/PATCH ProductOrder (camelCase keys per controller).
 * Pass resolved COA ids from {@link resolveCoaSubheadIdForPayload} after validation.
 *
 * @param {Record<string, unknown>} form
 * @param {{ branchId: number, sourceCoa: number, destinationCoa: number }} ctx
 */
export function buildProductOrderPayload(form, ctx) {
	let circle = normalizeStandingOrderCircle(form.circle);
	if (circle === "annual") {
		circle = "annually";
	}
	const amount =
		form.amount != null && form.amount !== ""
			? Number(String(form.amount).replace(/,/g, ""))
			: NaN;
	const payload = {
		branchId: Number(ctx.branchId),
		customerCode: normalizeCustomerCode(form.ordering_customer_code),
		sourceAccount: normalizeAccountNumber(
			form.source_account &&
				typeof form.source_account === "object" &&
				/** @type {{ account_number?: string }} */ (form.source_account)
					.account_number,
		),
		destinationAccount: normalizeAccountNumber(
			form.destination_account &&
				typeof form.destination_account === "object" &&
				/** @type {{ account_number?: string }} */ (form.destination_account)
					.account_number,
		),
		sourceCoa: Number(ctx.sourceCoa),
		destinationCoa: Number(ctx.destinationCoa),
		amount,
		startDate: String(form.start_date || "").trim() || undefined,
		endDate: String(form.end_date || "").trim() || undefined,
	};
	if (circle) {
		payload.circle = circle;
	}
	if (
		form.status != null &&
		String(form.status).trim() !== ""
	) {
		payload.status = String(form.status).trim();
	}
	return payload;
}
